/**
 * What this browser remembers about who is playing.
 *
 * Almost nothing, on purpose. Identity is the SERVER's: every paid, seated or result-bearing request is made by
 * the signed-in session and the server takes the player from that session, not from anything sent in a body or
 * held here. An id kept in `localStorage` used to decide whose seat a round was; after accounts it could
 * disagree with the account that had been charged (clear site data, sign back in), and the paid seat could then
 * never be opened.
 *
 * The one thing that survives is a read-once MIGRATION: a browser that played before accounts existed holds the
 * uuid its old rounds were recorded under. It is offered to `/api/auth/guest` exactly once, so a returning
 * player's guest is created under that history — and then dropped, because from that moment the session is
 * the identity. It is never minted here; a new browser holds none.
 */

const STORE_ID = 'wild21.player.id';
const STORE_NAME = 'wild21.player.name';

/** The URL parameter a shared lobby link carries. */
export const LOBBY_PARAM = 'lobby';

function read(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    // Safari in private mode throws rather than returning null.
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* not being able to remember is survivable; not being able to play is not */
  }
}

/**
 * The uuid an older version of this game stored, if this browser has one — the history to adopt when the guest
 * is first created (see the file header). Read-only: nothing here ever mints or refreshes it.
 */
export function legacyPlayerId(): string | null {
  return read(STORE_ID);
}

/** The migration is done once an account is known: forget the old id, for good. */
export function dropLegacyPlayerId(): void {
  try {
    globalThis.localStorage?.removeItem(STORE_ID);
  } catch {
    /* nothing to forget where nothing can be stored */
  }
}

/** The name this player last used, or a blank. */
export function playerName(): string {
  return read(STORE_NAME) ?? '';
}

export function setPlayerName(name: string): void {
  write(STORE_NAME, name.trim().slice(0, 24));
}

/* -------------------------------------------------------------------- links -- */

/**
 * Read a lobby code out of the page URL.
 *
 * Accepts `?lobby=ABC234` and `#lobby=ABC234`. The hash form matters for static
 * hosts that will not rewrite unknown paths, and costs one extra line here.
 */
export function lobbyFromLocation(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const query = url.searchParams.get(LOBBY_PARAM);
  if (query) return query;
  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  const fromHash = new URLSearchParams(hash).get(LOBBY_PARAM);
  return fromHash ?? null;
}

/** The link to send someone so they can play the same deck. */
export function lobbyLink(code: string, href: string): string {
  const url = new URL(href);
  url.hash = '';
  url.searchParams.set(LOBBY_PARAM, code);
  return url.toString();
}

/**
 * Take the lobby code out of the address bar once it has been used.
 *
 * Without this a refresh re-joins, which is harmless, but a *reload after
 * playing* would look like the game had forgotten the result — and the shared
 * link outliving its purpose in the address bar is confusing on a phone where
 * the URL is barely visible anyway.
 */
export function clearLobbyFromLocation(): void {
  const history = globalThis.history;
  const location = globalThis.location;
  if (!history?.replaceState || !location) return;
  const url = new URL(location.href);
  if (!url.searchParams.has(LOBBY_PARAM)) return;
  url.searchParams.delete(LOBBY_PARAM);
  history.replaceState(null, '', url.toString());
}

/** Put a link on the clipboard, falling back to a selection people can copy. */
export async function copyLink(link: string): Promise<boolean> {
  try {
    await globalThis.navigator?.clipboard?.writeText(link);
    return true;
  } catch {
    return false;
  }
}

/**
 * Hand the link to the OS share sheet, which is what a phone should do.
 *
 * Returns false when there is no share sheet — a desktop, mostly — so the caller
 * can fall back to the clipboard rather than doing nothing.
 */
export async function shareLink(link: string, text: string): Promise<boolean> {
  const nav = globalThis.navigator as Navigator & {
    share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
  };
  if (typeof nav?.share !== 'function') return false;
  try {
    await nav.share({ title: '21 Wild', text, url: link });
    return true;
  } catch {
    // A cancelled share sheet lands here and must not look like a failure the
    // caller should route around.
    return true;
  }
}
